import { api } from './api';
import { DashboardStats } from '../types';

/**
 * Interface for date range filter
 */
export interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

/**
 * Interface for monthly statistics
 */
export interface MonthlyStats {
  mese: string | number;
  entrate: number;
  uscite: number;
  saldo: number;
  numeroMovimenti?: number;
}

/**
 * Interface for category statistics
 */
export interface CategoryStats {
  categoriaId: number;
  nome: string;
  categoriaNome?: string;
  colore?: string;
  totale: number;
  percentuale: number;
  percentualeDelTotale?: number;
  tipo: string;
  numeroMovimenti?: number;
  sottocategorie?: CategoryStats[];
}

/**
 * Interface for article category statistics
 */
export interface ArticleCategoryStats {
  categoriaArticoloId: number;
  categoriaArticoloNome: string;
  colore?: string;
  totale: number;
  numeroMovimenti: number;
  percentualeDelTotale: number;
}

/**
 * Interface for unpaid movements
 */
export interface UnpaidMovement {
  id: number;
  dataMovimento: string;
  /** Scadenza effettiva: dataScadenza del movimento, o dataMovimento se non impostata */
  dataScadenza: string | null;
  descrizione: string | null;
  clienteNome: string | null;
  categoriaNome: string | null;
  importo: number;
  tipo: string;
  giorniScadenza: number;
  isOverdue: boolean;
}

/**
 * Interface for province statistics (sales by province)
 */
export interface ProvinceStats {
  provincia: string;
  totale: number;
  numeroMovimenti: number;
  percentualeDelTotale: number;
}

/**
 * Interface for customer statistics (top customers)
 */
export interface CustomerStats {
  clienteId: number;
  clienteNome: string;
  codiceProgressivo?: string;
  totale: number;
  numeroMovimenti: number;
  percentualeDelTotale: number;
}

/**
 * Interface for payment type statistics
 */
export interface PaymentTypeStats {
  tipoPagamento: string;
  tipo: string; // ENTRATA or USCITA
  totale: number;
  numeroMovimenti: number;
  percentualeDelTotale: number;
}

/**
 * Interface for sold articles statistics
 */
export interface SoldArticleStats {
  articoloId: number;
  articoloNome: string;
  articoloCodice?: string;
  categoriaArticoloId?: number;
  categoriaArticoloNome?: string;
  quantitaVenduta: number;
  unitaMisura: string;
  prezzoMedioVendita: number;
  ricavoTotale: number;
  prezzoAcquisto: number;
  costoGestioneMedio: number;
  costoTotale: number;
  ricavoNetto: number;
  numeroMovimenti: number;
  percentualeSulTotale: number;
}

/**
 * Interface for sold articles summary
 */
export interface SoldArticlesSummary {
  articoliDistinti: number;
  quantitaTotale: number;
  ricavoTotale: number;
  ricavoMedioPerArticolo: number;
  costoTotale: number;
  ricavoNettoTotale: number;
  numeroMovimentiTotali: number;
}

/**
 * Interface for customer report row (report clienti dettagliato)
 */
export interface CustomerReportStats {
  clienteId: number;
  clienteNome: string;
  codiceProgressivo?: string;
  tipoContatto?: string;
  citta?: string;
  provincia?: string;
  email?: string;
  telefono?: string;
  totale: number;
  totaleIncassato: number;
  totaleDaIncassare: number;
  numeroMovimenti: number;
  quantitaTotale: number;
  articoliDistinti: number;
  scontrinoMedio: number;
  primoAcquisto?: string;
  ultimoAcquisto?: string;
  giorniDaUltimoAcquisto?: number;
  percentualeSulTotale: number;
  nuovoCliente: boolean;
  articoloTopNome?: string;
  articoloTopRicavo?: number;
}

/**
 * Interface for customer report summary cards
 */
export interface CustomerReportSummary {
  clientiAttivi: number;
  nuoviClienti: number;
  fatturatoTotale: number;
  totaleDaIncassare: number;
  ordiniTotali: number;
  ticketMedio: number;
  fatturatoMedioPerCliente: number;
  ordiniMediPerCliente: number;
  quantitaTotale: number;
  clienteTopNome?: string;
  clienteTopTotale: number;
  concentrazioneTop5: number;
}

/**
 * Interface for the cliente x articolo matrix (chi ha comprato che cosa)
 */
export interface CustomerArticleStats {
  clienteId: number;
  clienteNome: string;
  /** null per le voci senza articolo, raggruppate per reparto */
  articoloId: number | null;
  /** Nome articolo, oppure nome reparto per le voci senza articolo */
  articoloNome: string;
  articoloCodice?: string;
  categoriaArticoloNome?: string;
  unitaMisura: string;
  quantita: number;
  ricavo: number;
  prezzoMedio: number;
  numeroMovimenti: number;
  ultimoAcquisto?: string;
  repartoId?: number | null;
  repartoNome?: string | null;
  /** true se la riga raggruppa incassi senza articolo (spedizioni, annunci, ...) */
  voceReparto: boolean;
}

/**
 * Interface for the full customer report response
 */
export interface CustomerReportResponse {
  summary: CustomerReportSummary;
  clienti: CustomerReportStats[];
  dettaglioArticoli: CustomerArticleStats[];
}

export type CustomerReportStatoPagamento = 'TUTTI' | 'PAGATI' | 'NON_PAGATI';
export type CustomerReportSortBy = 'TOTALE' | 'ORDINI' | 'QUANTITA' | 'TICKET_MEDIO' | 'ULTIMO_ACQUISTO' | 'NOME';

/**
 * Interface for customer report filters
 */
export interface CustomerReportFilters {
  clienteId?: number;
  articoloId?: number;
  categoriaArticoloId?: number;
  provincia?: string;
  tipoContatto?: string;
  statoPagamento?: CustomerReportStatoPagamento;
  sortBy?: CustomerReportSortBy;
  limit?: number;
}

/**
 * Get general dashboard statistics
 */
const getStats = async (dateRange?: DateRangeFilter): Promise<DashboardStats> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<DashboardStats>('/dashboard/stats', { params });
  return response.data;
};

/**
 * Get monthly statistics for a specific year or date range
 */
const getMonthlyStats = async (year?: number, dateRange?: DateRangeFilter): Promise<MonthlyStats[]> => {
  const params: Record<string, string | number> = {};

  if (dateRange?.startDate && dateRange?.endDate) {
    params.startDate = dateRange.startDate;
    params.endDate = dateRange.endDate;
  } else {
    params.year = year || new Date().getFullYear();
  }

  const response = await api.get<MonthlyStats[]>('/dashboard/monthly-stats', { params });
  return response.data;
};

/**
 * Get statistics by category
 */
const getCategoryStats = async (dateRange?: DateRangeFilter): Promise<CategoryStats[]> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<CategoryStats[]>('/dashboard/category-stats', { params });
  return response.data;
};

/**
 * Get unpaid movements ordered by due date (overdue first).
 * Without a limit the whole outstanding schedule is returned.
 */
const getUnpaidMovements = async (limit?: number): Promise<UnpaidMovement[]> => {
  const response = await api.get<UnpaidMovement[]>('/dashboard/unpaid-movements', {
    params: limit ? { limit } : undefined,
  });
  return response.data;
};

/**
 * Get statistics by article category
 */
const getArticleCategoryStats = async (dateRange?: DateRangeFilter): Promise<ArticleCategoryStats[]> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<ArticleCategoryStats[]>('/dashboard/article-category-stats', { params });
  return response.data;
};

/**
 * Get statistics by province (sales/orders grouped by province)
 */
const getProvinceStats = async (dateRange?: DateRangeFilter): Promise<ProvinceStats[]> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<ProvinceStats[]>('/dashboard/province-stats', { params });
  return response.data;
};

/**
 * Get top customers by sales volume
 */
const getTopCustomers = async (limit: number = 5, dateRange?: DateRangeFilter): Promise<CustomerStats[]> => {
  const params: Record<string, string | number> = { limit };
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<CustomerStats[]>('/dashboard/top-customers', { params });
  return response.data;
};

/**
 * Get statistics by payment type
 */
const getPaymentTypeStats = async (dateRange?: DateRangeFilter): Promise<PaymentTypeStats[]> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<PaymentTypeStats[]>('/dashboard/payment-type-stats', { params });
  return response.data;
};

/**
 * Get sold articles statistics
 */
const getSoldArticlesStats = async (
  limit: number = 10,
  dateRange?: DateRangeFilter
): Promise<SoldArticleStats[]> => {
  const params: Record<string, string | number> = { limit };
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<SoldArticleStats[]>('/dashboard/sold-articles-stats', { params });
  return response.data;
};

/**
 * Get sold articles summary
 */
const getSoldArticlesSummary = async (
  dateRange?: DateRangeFilter
): Promise<SoldArticlesSummary> => {
  const params: Record<string, string> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;

  const response = await api.get<SoldArticlesSummary>('/dashboard/sold-articles-summary', { params });
  return response.data;
};

/**
 * Get the detailed customer report (con filtri incrociati articolo/merceologia/zona)
 */
const getCustomerReport = async (
  filters?: CustomerReportFilters,
  dateRange?: DateRangeFilter
): Promise<CustomerReportResponse> => {
  const params: Record<string, string | number> = {};
  if (dateRange?.startDate) params.startDate = dateRange.startDate;
  if (dateRange?.endDate) params.endDate = dateRange.endDate;
  if (filters?.clienteId) params.clienteId = filters.clienteId;
  if (filters?.articoloId) params.articoloId = filters.articoloId;
  if (filters?.categoriaArticoloId) params.categoriaArticoloId = filters.categoriaArticoloId;
  if (filters?.provincia) params.provincia = filters.provincia;
  if (filters?.tipoContatto) params.tipoContatto = filters.tipoContatto;
  if (filters?.statoPagamento) params.statoPagamento = filters.statoPagamento;
  if (filters?.sortBy) params.sortBy = filters.sortBy;
  if (filters?.limit) params.limit = filters.limit;

  const response = await api.get<CustomerReportResponse>('/dashboard/customer-report', { params });
  return response.data;
};

const dashboardService = {
  getStats,
  getMonthlyStats,
  getCategoryStats,
  getUnpaidMovements,
  getArticleCategoryStats,
  getProvinceStats,
  getTopCustomers,
  getPaymentTypeStats,
  getSoldArticlesStats,
  getSoldArticlesSummary,
  getCustomerReport,
};

export default dashboardService;
